import {FC, useRef, useState} from 'react';
import {NavLink, useLocation, useNavigate} from 'react-router-dom';
//mui
import {Box} from '@mui/material';
//icons
import {ReactComponent as PlusIcon} from '@icons/plus.svg';
import {ReactComponent as ArrowDownIcon} from '@icons/select-arrow-down.svg';
import {ReactComponent as DashboardIcon} from '@icons/sidebar/dashboard.svg';
import {ReactComponent as TicketIcon} from '@icons/sidebar/ticket.svg';
import {ReactComponent as PeopleIcon} from '@icons/sidebar/people.svg';
import {ReactComponent as JudgeIcon} from '@icons/sidebar/judge.svg';
import {ReactComponent as SmsIcon} from '@icons/sidebar/sms.svg';
import {ReactComponent as DirectboxIcon} from '@icons/sidebar/directbox-default.svg';
import {ReactComponent as WarningIcon} from '@icons/sidebar/warning-2.svg';
import {ReactComponent as SettingIcon} from '@icons/sidebar/setting.svg';
import {ReactComponent as QuestionIcon} from '@icons/sidebar/question.svg';
import {ReactComponent as SendEmailIcon} from '@icons/sidebar/send-email.svg';
import {ReactComponent as AddSquareIcon} from '@icons/sidebar/add-square.svg';
import {ReactComponent as MedalIcon} from '@icons/medal-star.svg';
import {ReactComponent as MoneyIcon} from '@icons/sidebar/money.svg';
//constants
import {
  ADD_ITEM,
  ADD_TICKET,
  ADD_TICKETS,
  ADD_WINNER,
  CANCEL,
  CREATE_FIRST_EVENT,
  CREATE_NEW_EVENT,
  EVENT_ID,
  OUTLINE,
  SAVE_CHANGES,
  SEND,
  SEND_EMAIL,
  SIDEBAR,
} from '@/utils/constants';
//enums
import {EMAIL_TEMPLATE_TYPE, ITeamRole, ROUTER_URL} from '@/utils/enums';
//mui
import {TreeItem, TreeView} from '@mui/x-tree-view';
//components
import Button from '@components/Button';
import Modal from '@components/Modal';
import SendEmail from '@/components/Organization/Dashboard/SendEmail';
import CreateNewEvent, {CreateNewEventHandle} from '@/components/Organization/Dashboard/CreateNewEvent';
//styles
import {organizationSidebarStyle} from './OrganizationSidebar.styles';
import {getCurrentEventSelector} from '@/store/organization-event';
import AddTableTicket from '@/components/Organization/TablesAndTickets/AddTableTicket';
import {tablesAndTicketsTableStyle} from '@/components/Organization/TablesAndTickets/TablesAndTicketsTable/TablesAndTicketsTable.styles';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {toast} from 'react-toastify';
import {getDefaultRequestBody, getErrorMessage} from '@/utils/helpers';
import {getTicketsList, getTicketsTiers} from '@/store/organization-tables';
import {CommunicationService} from '@/utils/services/organization-communication.services';
import {orgAPI} from '@/core/api';
import AuctionItem from '@/components/Organization/Auction/AuctionTable/AuctionItem';
import {auctionTableStyle} from '@/components/Organization/Auction/AuctionTable/AuctionTable.styles';
import AddWinnerPage from '@/pages/Organization/AddWinnerPage';
import {WinnerTableStyle} from '@/components/Organization/AuctionEntry/AuctionWinnerTable/WinnerTable/WinnerTableStyle.styles';
import {getDashboardStatisticsSelector} from '@/store/organization-dashboard';
import {getTeamSelector} from '@/store/organization';

const OrganizationSidebar: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [isShowSendEmailModal, setIsShowSendEmailModal] = useState(false);
  const [isShowNewEventModal, setIsShowNewEventModal] = useState(false);
  const [isShowAddTicketModal, setIsShowAddTicketModal] = useState<boolean>(false);
  const [selectedEmail, setSelectedEmail] = useState(EMAIL_TEMPLATE_TYPE.TABLE_UPDATE);
  const [isShowAuctionModal, setIsShowAuctionModal] = useState<boolean>(false);
  const [isAddWinnerModalOpen, setIsAddWinnerModalOpen] = useState(false);
  const [isNoifyModalOpen, setIsNoifyModalOpen] = useState(false);
  const [notifyMessage, setNotifyMessage] = useState('');

  const formikRef = useRef<any>(null);
  const createEventRef = useRef<CreateNewEventHandle>(null);

  const currentEvent = useAppSelector(getCurrentEventSelector);
  const statistics = useAppSelector(getDashboardStatisticsSelector);
  const team = useAppSelector(getTeamSelector);

  const isStandartMember = !!team && team.role === ITeamRole.STANDART;

  const handleCloseEmailModal = () => setIsShowSendEmailModal(false);
  const handleCloseEventModal = () => setIsShowNewEventModal(false);
  const handleModalCloseAttempt = () => {
    if (createEventRef.current) {
      createEventRef.current.requestClose();
    } else {
      handleCloseEventModal();
    }
  };

  const handleAddWinner = async () => {
    if (formikRef.current) {
      await formikRef.current.handleSubmit();
    }
  };

  const handleSendEmail = async () => {
    if (!currentEvent) return;
    try {
      if (selectedEmail === EMAIL_TEMPLATE_TYPE.INVOICE_EMAIL) {
        await CommunicationService.sendInvoiceEmailTemplate(currentEvent.id);
      } else {
        await CommunicationService.sendEmailTemplate(currentEvent.id, selectedEmail);
      }
      toast.success('Email sent successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const getTableData = async () => {
    if (!currentEvent?.id) return;

    try {
      const reqBody = getDefaultRequestBody(currentEvent!.id, EVENT_ID);

      await Promise.all([
        dispatch(getTicketsList({reqBody, apiInstance: orgAPI})).unwrap(),
        dispatch(getTicketsTiers({reqBody: currentEvent.id, apiInstance: orgAPI})).unwrap(),
      ]);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleAddTicketModalChange = () => {
    if (!currentEvent?.tableInfos.length) {
      handleNotifyModalChange('Add a table tier before you can assign guests to a table.');
      return;
    }
    if (!isShowAddTicketModal) getTableData();
    setIsShowAddTicketModal(!isShowAddTicketModal);
  };

  const handleAuctionModalChange = () => setIsShowAuctionModal(!isShowAuctionModal);
  const handleWinnerModal = () => setIsAddWinnerModalOpen(!isAddWinnerModalOpen);
  const handleNotifyModalChange = (message?: string) => {
    if (message) setNotifyMessage(message);
    setIsNoifyModalOpen(!isNoifyModalOpen);
  };
  const handleCloseNotifyModal = () => {
    setNotifyMessage('');
    setIsNoifyModalOpen(false);
  };

  const getSelectedNode = () => {
    switch (location.pathname) {
      case ROUTER_URL.ORGANIZATION_COMMUNICATIONS_CUSTOMIZATION:
        return '2';
      case ROUTER_URL.ORGANIZATION_COMMUNICATIONS_TICKETS:
        return '3';
      case ROUTER_URL.ORGANIZATION_COMMUNICATIONS_TICKET_PAYMENTS:
        return '4';
      case ROUTER_URL.ORGANIZATION_COMMUNICATIONS_TABLE_UPDATE:
        return '5';
      case ROUTER_URL.ORGANIZATION_COMMUNICATIONS_PATRON_UPDATE:
        return '6';
      case ROUTER_URL.ORGANIZATION_COMMUNICATIONS_AUCTION_EMAIL:
        return '7';
      case ROUTER_URL.ORGANIZATION_COMMUNICATIONS_INVOISE_EMAIL:
        return '8';
      case ROUTER_URL.ORGANIZATION_COMMUNICATIONS_PAYMENT_ACKNOWLEDGEMENT:
        return '9';
      case ROUTER_URL.ORGANIZATION_COMMUNICATIONS_CONTRIBUTIONS_SUMMARY:
        return '10';

      default:
        return null;
    }
  };

  const getPrintableSelectedNode = () => {
    const path = location.pathname;

    if (path.includes(ROUTER_URL.ORGANIZATION_PRINTABLES_CUSTOMIZATION)) return '11';
    if (path.includes(ROUTER_URL.ORGANIZATION_PRINTABLES_QR)) return '12';
    if (path.includes(ROUTER_URL.ORGANIZATION_PRINTABLES_BID_SHEETS)) return '13';
    if (path.includes(ROUTER_URL.ORGANIZATION_PRINTABLES_GUESTS_BY_TABLE)) return '14';
    if (path.includes(ROUTER_URL.ORGANIZATION_PRINTABLES_GUESTS_BY_NAME)) return '15';

    return null;
  };

  const handleTableClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!currentEvent) return;
    if (!currentEvent.tableInfos.length) {
      handleNotifyModalChange('Add a table tier before you can assign guests to a table.');
      return;
    }
    handleAddTicketModalChange();
  };

  const handleAuctionClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!currentEvent) return;
    handleAuctionModalChange();
  };

  const handleAddWinnerClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!currentEvent) return;

    if (!currentEvent.tableInfos.length) {
      handleNotifyModalChange('Add a table tier before you can assign winners.');
      return;
    }
    if (!statistics?.allGuests) {
      handleNotifyModalChange('Add attendees before you can assign winners.');
      return;
    }
    if (!statistics.allItems) {
      handleNotifyModalChange('Add auction items before you can assign winners.');
      return;
    }

    handleWinnerModal();
  };

  return (
    <Box sx={organizationSidebarStyle.wrap}>
      <Box sx={organizationSidebarStyle.spacer} />
      <Box sx={organizationSidebarStyle.sidebar}>
        <Button
          startIcon={<PlusIcon />}
          title={!currentEvent ? CREATE_FIRST_EVENT : CREATE_NEW_EVENT}
          sx={organizationSidebarStyle.buttonCreate}
          onClick={() => setIsShowNewEventModal(true)}
          disableRipple
          disabled={isStandartMember}
        />
        <Button
          onClick={handleAddTicketModalChange}
          title={ADD_TICKET}
          startIcon={<PlusIcon />}
          disabled={!currentEvent || isStandartMember}
          disableRipple
        />
        <Button
          startIcon={<SendEmailIcon />}
          variant={OUTLINE}
          title={SEND_EMAIL}
          onClick={() => setIsShowSendEmailModal(true)}
          disableRipple
          disabled={!currentEvent}
          sx={!currentEvent ? organizationSidebarStyle.disabledButton : {}}
        />

        <Box sx={organizationSidebarStyle.menu}>
          <NavLink to={ROUTER_URL.ORGANIZATION_DASHBOARD} style={organizationSidebarStyle.menuItem} end>
            <DashboardIcon /> {SIDEBAR.DASHBOARD}
          </NavLink>
          <NavLink to={ROUTER_URL.ORGANIZATION_TABLES} style={organizationSidebarStyle.menuItem} end>
            <TicketIcon /> {SIDEBAR.TABLES_TICKETS} <Box sx={organizationSidebarStyle.spacer} />{' '}
            <Box sx={!currentEvent || isStandartMember ? organizationSidebarStyle.iconDisabled : organizationSidebarStyle.icon}>
              <AddSquareIcon onClick={(e) => handleTableClick(e)} />
            </Box>
          </NavLink>
          <NavLink to={ROUTER_URL.ORGANIZATION_ATTENDEES} style={organizationSidebarStyle.menuItem} end>
            <PeopleIcon /> {SIDEBAR.ATTENDEES}
          </NavLink>
          <NavLink to={ROUTER_URL.ORGANIZATION_AUCTION} style={organizationSidebarStyle.menuItem}>
            <JudgeIcon /> {SIDEBAR.AUCTION} <Box sx={organizationSidebarStyle.spacer} />{' '}
            <Box sx={!currentEvent ? organizationSidebarStyle.iconDisabled : organizationSidebarStyle.icon}>
              <AddSquareIcon onClick={(e) => handleAuctionClick(e)} />
            </Box>
          </NavLink>
          <NavLink to={ROUTER_URL.ORGANIZATION_AUCTION_ENTRY} style={organizationSidebarStyle.menuItem}>
            <MedalIcon /> {SIDEBAR.WINNER_ENTRY} <Box sx={organizationSidebarStyle.spacer} />{' '}
            <Box sx={!currentEvent ? organizationSidebarStyle.iconDisabled : organizationSidebarStyle.icon}>
              <AddSquareIcon onClick={(e) => handleAddWinnerClick(e)} />
            </Box>
          </NavLink>
          <NavLink to={ROUTER_URL.ORGANIZATION_CHECK_OUT} style={organizationSidebarStyle.menuItem}>
            <JudgeIcon /> {SIDEBAR.AUCTION_CHECKOUT} <Box sx={organizationSidebarStyle.spacer} />
          </NavLink>

          <NavLink to={ROUTER_URL.ORGANIZATION_FUND} style={organizationSidebarStyle.menuItem}>
            <MoneyIcon /> {SIDEBAR.FUND_THE_MISSION}
          </NavLink>

          <Box sx={organizationSidebarStyle.menuTreeItem}>
            <TreeView
              sx={organizationSidebarStyle.tree}
              defaultCollapseIcon={<ArrowDownIcon style={organizationSidebarStyle.arrowTop} />}
              defaultExpandIcon={<ArrowDownIcon />}
              selected={getSelectedNode()}
            >
              <TreeItem
                nodeId="1"
                label={
                  <Box>
                    <SmsIcon /> <Box>{SIDEBAR.COMMUNICATIONS}</Box>
                  </Box>
                }
              >
                <TreeItem
                  nodeId="2"
                  label={
                    <NavLink to={ROUTER_URL.ORGANIZATION_COMMUNICATIONS_CUSTOMIZATION} end>
                      {SIDEBAR.COMMUNICATIONS_CUSTOMIZATION}
                    </NavLink>
                  }
                />
                <TreeItem
                  nodeId="3"
                  label={
                    <NavLink to={ROUTER_URL.ORGANIZATION_COMMUNICATIONS_TICKETS} end>
                      {SIDEBAR.TICKET_INVOICE}
                    </NavLink>
                  }
                />
                <TreeItem
                  nodeId="4"
                  label={
                    <NavLink to={ROUTER_URL.ORGANIZATION_COMMUNICATIONS_TICKET_PAYMENTS} end>
                      {SIDEBAR.TICKET_PAYMENT_CONFIRMATION}
                    </NavLink>
                  }
                />
                <TreeItem
                  nodeId="5"
                  label={
                    <NavLink to={ROUTER_URL.ORGANIZATION_COMMUNICATIONS_TABLE_UPDATE} end>
                      {SIDEBAR.TABLE_UPDATE_EMAIL}
                    </NavLink>
                  }
                />
                <TreeItem
                  nodeId="6"
                  label={
                    <NavLink to={ROUTER_URL.ORGANIZATION_COMMUNICATIONS_PATRON_UPDATE} end>
                      {SIDEBAR.PATRON_UPDATE_EMAIL}
                    </NavLink>
                  }
                />
                <TreeItem
                  nodeId="7"
                  label={
                    <NavLink to={ROUTER_URL.ORGANIZATION_COMMUNICATIONS_AUCTION_EMAIL} end>
                      {SIDEBAR.AUCTION_EMAIL}
                    </NavLink>
                  }
                />
                <TreeItem
                  nodeId="8"
                  label={
                    <NavLink to={ROUTER_URL.ORGANIZATION_COMMUNICATIONS_INVOISE_EMAIL} end>
                      {SIDEBAR.INVOICE_EMAIL}{' '}
                    </NavLink>
                  }
                />
                <TreeItem
                  nodeId="9"
                  label={
                    <NavLink to={ROUTER_URL.ORGANIZATION_COMMUNICATIONS_PAYMENT_ACKNOWLEDGEMENT} end>
                      {SIDEBAR.PAYMENT_ACKNOWLEDGEMENT}
                    </NavLink>
                  }
                />
                <TreeItem
                  nodeId="10"
                  label={
                    <NavLink to={ROUTER_URL.ORGANIZATION_COMMUNICATIONS_CONTRIBUTIONS_SUMMARY} end>
                      {SIDEBAR.CONTRIBUTIONS_SUMMARY}
                    </NavLink>
                  }
                />
              </TreeItem>
            </TreeView>
          </Box>

          <Box sx={organizationSidebarStyle.menuTreeItem}>
            <TreeView
              sx={organizationSidebarStyle.tree}
              defaultCollapseIcon={<ArrowDownIcon style={organizationSidebarStyle.arrowTop} />}
              defaultExpandIcon={<ArrowDownIcon />}
              selected={getPrintableSelectedNode()}
            >
              <TreeItem
                className="firstMainTree"
                nodeId="19"
                label={
                  <Box>
                    <DirectboxIcon /> <Box>{SIDEBAR.PRINTABLES}</Box>
                  </Box>
                }
              >
                <TreeItem
                  nodeId="11"
                  label={
                    <NavLink to={ROUTER_URL.ORGANIZATION_PRINTABLES_CUSTOMIZATION} end>
                      {SIDEBAR.SETTINGS}{' '}
                    </NavLink>
                  }
                />
                <TreeItem
                  nodeId="12"
                  label={
                    <NavLink to={ROUTER_URL.ORGANIZATION_PRINTABLES_QR} end>
                      {SIDEBAR.WEBSITE_CODE}
                    </NavLink>
                  }
                />
                <TreeItem
                  nodeId="13"
                  label={
                    <NavLink to={ROUTER_URL.ORGANIZATION_PRINTABLES_BID_SHEETS} end>
                      {SIDEBAR.BID_SHEETS}
                    </NavLink>
                  }
                />
                <TreeItem
                  nodeId="14"
                  label={
                    <NavLink to={ROUTER_URL.ORGANIZATION_PRINTABLES_GUESTS_BY_TABLE} end>
                      {SIDEBAR.GUEST_LIST_BY_TABLE}
                    </NavLink>
                  }
                />
                <TreeItem
                  nodeId="15"
                  label={
                    <NavLink to={ROUTER_URL.ORGANIZATION_PRINTABLES_GUESTS_BY_NAME} end>
                      {SIDEBAR.GUEST_LIST_BY_LAST_NAME}
                    </NavLink>
                  }
                />
              </TreeItem>
            </TreeView>
          </Box>

          <NavLink to={ROUTER_URL.ORGANIZATION_REPORTS} style={organizationSidebarStyle.menuItem}>
            <WarningIcon /> {SIDEBAR.REPORTS}
          </NavLink>

          <NavLink
            to={ROUTER_URL.ORGANIZATION_EVENT_SETTINGS}
            onClick={(e) => {
              if (!currentEvent) {
                e.preventDefault();
              }
            }}
            style={{...organizationSidebarStyle.menuItem, ...(!currentEvent ? organizationSidebarStyle.disabled : {})}}
          >
            <SettingIcon /> {SIDEBAR.EVENT_SETTINGS}
          </NavLink>

          <NavLink to={ROUTER_URL.ORGANIZATION_HELP_AND_SUPPORT} style={organizationSidebarStyle.menuItem}>
            <QuestionIcon /> {SIDEBAR.HELP_SUPPORT}
          </NavLink>
        </Box>
      </Box>

      <Modal
        onClose={handleCloseEmailModal}
        onSave={handleSendEmail}
        open={isShowSendEmailModal}
        sx={organizationSidebarStyle.sendMailModal}
        mainButtonTitle={SEND}
        isShowCloseButton
        isShowSaveButton
        title={SEND_EMAIL}
      >
        <SendEmail selectedEmail={selectedEmail} setSelectedEmail={setSelectedEmail} />
      </Modal>

      <Modal
        onClose={handleModalCloseAttempt}
        open={isShowNewEventModal}
        sx={organizationSidebarStyle.newEventModal}
        title={CREATE_NEW_EVENT}
      >
        <CreateNewEvent onCloseModal={handleCloseEventModal} ref={createEventRef} />
      </Modal>

      <Modal
        onClose={handleAddTicketModalChange}
        open={isShowAddTicketModal}
        sx={tablesAndTicketsTableStyle.modal}
        title={ADD_TICKETS}
      >
        <AddTableTicket onCloseModal={handleAddTicketModalChange} />
      </Modal>

      <Modal
        onClose={handleAuctionModalChange}
        open={isShowAuctionModal}
        sx={auctionTableStyle.auctionModal}
        title={ADD_ITEM}
      >
        <AuctionItem
          onCloseModal={handleAuctionModalChange}
          editableAuction={null}
          onSuccess={() => navigate(ROUTER_URL.ORGANIZATION_AUCTION)}
        />
      </Modal>

      <Modal
        open={isAddWinnerModalOpen}
        onClose={handleWinnerModal}
        onSave={handleAddWinner}
        sx={WinnerTableStyle.modal}
        mainButtonTitle={SAVE_CHANGES}
        closeButtonTitle={CANCEL}
        isShowSaveButton
        isShowCloseButton
        title={ADD_WINNER}
        isButtonsBottom
      >
        <AddWinnerPage formRef={formikRef} isModal={true} closeModal={handleWinnerModal} />
      </Modal>
      <Modal
        onClose={handleCloseNotifyModal}
        open={isNoifyModalOpen}
        title="Action needed"
        description={notifyMessage}
        sx={tablesAndTicketsTableStyle.modalNotify}
      />
    </Box>
  );
};

export default OrganizationSidebar;
